import { isApiError } from '@/api/errors';

/**
 * zod 校验结果的结构化视图。
 *
 * 前端只消费 `@en3/shared` 导出的 schema，不直接依赖 zod 包，
 * 因此这里用结构化类型而不是 `import type { z } from 'zod'` —— 少一个直接依赖，
 * 也避免"两个包各自锁一个 zod 版本"的隐患。
 */
interface ZodLikeError {
  issues: readonly { path: readonly (string | number | symbol)[]; message: string }[];
}

/**
 * 表单校验工具：把 zod 的 issues 摊平成 `{ 字段名: 中文提示 }`。
 * 只保留每个字段的第一条错误，避免同一输入框下堆多行提示。
 */
export function zodFieldErrors(error: ZodLikeError): Record<string, string> {
  const out: Record<string, string> = {};
  for (const issue of error.issues) {
    const key = String(issue.path[0] ?? '_form');
    if (!out[key]) out[key] = issue.message;
  }
  return out;
}

/**
 * 服务端校验错误 → 字段错误。
 * 后端的 `VALIDATION_FAILED` 会把 zod issues 放进 `details.issues`，字段名与前端一致，
 * 因此可以直接复用同一套展示逻辑（这就是前后端共用同一份 schema 的好处）。
 */
export function serverFieldErrors(error: unknown): Record<string, string> {
  if (!isApiError(error)) return {};
  const out: Record<string, string> = {};
  for (const issue of error.fieldIssues) {
    const key = issue.path || '_form';
    if (!out[key]) out[key] = issue.message;
  }
  return out;
}
